import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useApi } from '../../services/api/provider';
import { ProductService } from '../../services/api/domains/products/service';
import {
  ExternalLinkSearchResult,
  ExternalLinkSource,
} from '../../services/api/domains/products/types';
import {
  DEBOUNCE_DELAY,
  DEFAULT_PAGE_SIZE,
} from '../../shared/hooks/useAsyncSelect';
import {
  getStagedRecordKey,
  getSummaryFields,
  resolveExternalLinkFieldValue,
} from '../../domains/products/adapters';
import LgmButton from '../LgmButton';
import {
  GRAY_200,
  GRAY_300,
  GRAY_500,
  GRAY_50,
  GRAY_900,
} from 'theme/grayScale';
import {
  COLOR_DANGER_TEXT,
  COLOR_INFO_BG,
  COLOR_INFO_TEXT,
} from 'theme/colors';

// ---------------------------------------------------------------------------
// ExternalLinkSearchPane — issue #622, Sub-task 9.2
// ---------------------------------------------------------------------------

interface ExternalLinkSearchPaneProps {
  source: ExternalLinkSource;
  sourceLabel: string;
  /** Scopes each result's `is_linked` flag to this product (issue #681, item 2) — see `ProductService.searchExternalLinkSource`. */
  productId: number;
  /** `(source, record_id)` keys (see `getStagedRecordKey`) already in the review list, so a checked row here stays checked after a re-search. */
  stagedKeys: Set<string>;
  onToggleRecord: (record: ExternalLinkSearchResult, checked: boolean) => void;
}

type SearchPhase = 'idle' | 'loading' | 'ready' | 'error';

/**
 * Type-ahead search of one source's names for the "Link to Source" modal
 * (Sub-task 9.2). Debounced and paginated using `useAsyncSelect`'s existing
 * 300ms/page-size conventions (imported constants, not re-declared) — the
 * hook itself isn't reusable as-is here since it's built around react-
 * select's `loadOptions(inputValue, callback)` contract and
 * `fetchAsyncSelectOptions`'s `limit`/`offset`/`advancedSearch` query shape,
 * whereas `GET /api/external-link-search/:source` takes `q`/`page`/
 * `pageSize` and returns `{ results, matched_field, matched_value, ... }` —
 * a genuinely different request/response shape, not a drop-in fit for a
 * react-select dropdown.
 */
const ExternalLinkSearchPane: React.FC<ExternalLinkSearchPaneProps> = ({
  source,
  sourceLabel,
  productId,
  stagedKeys,
  onToggleRecord,
}) => {
  const api = useApi();
  const productService = useMemo(() => new ProductService(api), [api]);

  const [query, setQuery] = useState('');
  const [phase, setPhase] = useState<SearchPhase>('idle');
  const [results, setResults] = useState<ExternalLinkSearchResult[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  // Separate from `phase`/`errorMessage` on purpose (issue #622 exhaustive-
  // review follow-up): a failed "Load more" (page 2+) used to fall into
  // `phase === 'error'`, whose render branch shows ONLY the error + Retry —
  // the already-successfully-loaded page-1 rows disappeared from the screen
  // even though `results` still held them, and Retry restarted from page 1,
  // losing pagination progress. This lets append failures render alongside
  // the existing results, and retries the SAME failed page.
  const [loadMoreError, setLoadMoreError] = useState<string | null>(null);

  const debounceTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastRequestedTermRef = useRef('');
  // PERF-004: same pattern as the shared `useQuery` hook
  // (services/api/hooks.ts) — aborts the prior in-flight request before a
  // new one starts, so a superseded typeahead request is genuinely
  // cancelled server-side, not just its response discarded client-side.
  const abortControllerRef = useRef<AbortController | null>(null);

  useEffect(
    () => () => {
      if (debounceTimeoutRef.current) clearTimeout(debounceTimeoutRef.current);
      abortControllerRef.current?.abort();
    },
    []
  );

  const runSearch = useCallback(
    async (term: string, targetPage: number, append: boolean) => {
      abortControllerRef.current?.abort();
      const abortController = new AbortController();
      abortControllerRef.current = abortController;

      if (append) setIsLoadingMore(true);
      else setPhase('loading');
      setErrorMessage(null);
      setLoadMoreError(null);

      try {
        const response = await productService.searchExternalLinkSource(
          source,
          term,
          targetPage,
          DEFAULT_PAGE_SIZE,
          productId,
          abortController.signal
        );
        // Stale-response guard: `handleQueryChange`'s debounce check only
        // gates whether this call gets DISPATCHED. The abort above cancels
        // any PRIOR request when a new one starts, but this guard is still
        // needed for the reverse ordering — a request that was already
        // in flight when this one's abort() fired can still have its
        // `.then` scheduled before this response is applied (abort rejects
        // the request, it doesn't synchronously unschedule already-resolved
        // microtasks) — so a stale response could otherwise still land.
        if (term !== lastRequestedTermRef.current) return;
        setTotal(response.data.total);
        setPage(response.data.page);
        // The API response only carries `source` at the envelope level (see
        // api/sql/externalLinkSearch.js's return shape) -- each row must be
        // stamped with it here, or every source-keyed lookup downstream
        // (EXTERNAL_LINK_DISPLAY_FIELDS, getStagedRecordKey) reads undefined.
        // `source` is this pane's fixed prop (one discriminant for every row
        // it fetches), so the cast is a single TS-mandated hop -- the same
        // pattern resolveExternalLinkFieldValue documents -- not a
        // double-cast: the other fields per row already match that source.
        const stampedResults: ExternalLinkSearchResult[] =
          response.data.results.map(
            result => ({ ...result, source }) as ExternalLinkSearchResult
          );
        setResults(prev =>
          append ? [...prev, ...stampedResults] : stampedResults
        );
        setPhase('ready');
      } catch (err) {
        if (abortController.signal.aborted) return;
        if (term !== lastRequestedTermRef.current) return;
        const message =
          err instanceof Error ? err.message : 'Search failed. Try again.';
        if (append) {
          // Keep the already-rendered page(s) on screen and `phase` at
          // 'ready' — only the failed page's own load-more row shows an
          // error, matching the search pane's own already-established
          // convention of never letting a later failure erase earlier
          // successful state.
          setLoadMoreError(message);
        } else {
          setErrorMessage(message);
          setPhase('error');
          setResults([]);
          setTotal(0);
        }
      } finally {
        if (append) setIsLoadingMore(false);
      }
    },
    [productService, source, productId]
  );

  const handleQueryChange = (value: string) => {
    setQuery(value);
    if (debounceTimeoutRef.current) clearTimeout(debounceTimeoutRef.current);

    const trimmed = value.trim();
    lastRequestedTermRef.current = trimmed;
    if (!trimmed) {
      // PERF-004 review fix: clearing the input must abort any in-flight
      // request too -- otherwise typing then clearing quickly leaves a
      // server-side request running (holding a DB pool slot) with nothing
      // left to ever supersede it, undercutting this fix's whole goal.
      abortControllerRef.current?.abort();
      setPhase('idle');
      setResults([]);
      setTotal(0);
      setLoadMoreError(null);
      return;
    }

    debounceTimeoutRef.current = setTimeout(() => {
      // Only apply the result if this is still the latest-typed term — a
      // slower earlier request resolving after a newer one would otherwise
      // clobber fresher results.
      if (lastRequestedTermRef.current === trimmed) {
        runSearch(trimmed, 1, false);
      }
    }, DEBOUNCE_DELAY);
  };

  const handleRetry = () => runSearch(query.trim(), 1, false);
  const handleLoadMore = () => runSearch(query.trim(), page + 1, true);

  const summaryFields = getSummaryFields(source);
  const hasMore = results.length < total;

  return (
    <div style={styles.container}>
      <label style={styles.label} htmlFor="external-link-search-input">
        Search {sourceLabel}
      </label>
      <input
        id="external-link-search-input"
        type="text"
        value={query}
        onChange={e => handleQueryChange(e.target.value)}
        placeholder={`Search ${sourceLabel} by name…`}
        style={styles.input}
        aria-describedby="external-link-search-status"
      />

      <div
        id="external-link-search-status"
        style={styles.resultsArea}
        role="region"
        aria-live="polite">
        {phase === 'idle' && (
          <div style={styles.stateMessage}>
            Type at least one character to search {sourceLabel}.
          </div>
        )}

        {phase === 'loading' && (
          <div style={styles.stateMessage}>Searching…</div>
        )}

        {phase === 'error' && (
          <div style={styles.errorMessage} role="alert">
            <span>{errorMessage}</span>
            <LgmButton
              variant="outlined"
              onClick={handleRetry}
              style={{ marginTop: '8px' }}>
              Retry
            </LgmButton>
          </div>
        )}

        {phase === 'ready' && results.length === 0 && (
          <div style={styles.stateMessage}>
            No {sourceLabel} records match &quot;{query.trim()}&quot;.
          </div>
        )}

        {phase === 'ready' && results.length > 0 && (
          <>
            <ul
              style={styles.list}
              aria-label={`${sourceLabel} search results`}>
              {results.map(result => {
                const key = getStagedRecordKey(result);
                const checked = stagedKeys.has(key);
                return (
                  <li key={key} style={styles.row}>
                    <label style={styles.rowLabel}>
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={e => onToggleRecord(result, e.target.checked)}
                        aria-label={`Add ${result.matched_value} to review list`}
                      />
                      <div style={styles.rowContent}>
                        <span style={styles.rowTitle}>
                          {result.matched_value}
                          {result.is_linked && (
                            <span style={styles.linkedBadge}>
                              Already Linked
                            </span>
                          )}
                        </span>
                        <div style={styles.rowSummary}>
                          {summaryFields.map(field => (
                            <span key={field.key} style={styles.summaryField}>
                              <strong>{field.label}:</strong>{' '}
                              {resolveExternalLinkFieldValue(result, field)}
                            </span>
                          ))}
                        </div>
                      </div>
                    </label>
                  </li>
                );
              })}
            </ul>
            {loadMoreError && (
              <div style={styles.loadMoreError} role="alert">
                <span>{loadMoreError}</span>
              </div>
            )}
            {hasMore && (
              <LgmButton
                variant="outlined"
                onClick={handleLoadMore}
                disabled={isLoadingMore}
                style={{ marginTop: '8px', width: '100%' }}>
                {isLoadingMore
                  ? 'Loading more…'
                  : loadMoreError
                    ? 'Retry load more'
                    : `Load more (${results.length} of ${total})`}
              </LgmButton>
            )}
          </>
        )}
      </div>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem',
    flex: 1,
    minWidth: 0,
  },
  label: {
    fontSize: '0.8125rem',
    fontWeight: 600,
    color: GRAY_500,
  },
  input: {
    padding: '0.5rem 0.75rem',
    fontSize: '0.875rem',
    border: `1px solid ${GRAY_300}`,
    borderRadius: '4px',
    width: '100%',
  },
  resultsArea: {
    flex: 1,
    minHeight: '12rem',
    maxHeight: '24rem',
    overflowY: 'auto',
    border: `1px solid ${GRAY_200}`,
    borderRadius: '4px',
    padding: '0.5rem',
  },
  stateMessage: {
    padding: '1rem',
    fontSize: '0.8125rem',
    color: GRAY_500,
    textAlign: 'center',
  },
  errorMessage: {
    padding: '1rem',
    fontSize: '0.8125rem',
    color: COLOR_DANGER_TEXT,
    textAlign: 'center',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  loadMoreError: {
    marginTop: '8px',
    padding: '0.5rem',
    fontSize: '0.75rem',
    color: COLOR_DANGER_TEXT,
    textAlign: 'center',
  },
  list: {
    listStyle: 'none',
    margin: 0,
    padding: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: '0.375rem',
  },
  row: {
    border: `1px solid ${GRAY_200}`,
    borderRadius: '4px',
    backgroundColor: GRAY_50,
  },
  rowLabel: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '0.5rem',
    padding: '0.5rem',
    cursor: 'pointer',
  },
  rowContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.125rem',
    minWidth: 0,
  },
  rowTitle: {
    fontSize: '0.875rem',
    fontWeight: 600,
    color: GRAY_900,
  },
  linkedBadge: {
    marginLeft: '0.5rem',
    padding: '0.0625rem 0.375rem',
    fontSize: '0.6875rem',
    fontWeight: 600,
    color: COLOR_INFO_TEXT,
    backgroundColor: COLOR_INFO_BG,
    borderRadius: '4px',
    verticalAlign: 'middle',
  },
  rowSummary: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '0.75rem',
    fontSize: '0.75rem',
    color: GRAY_500,
  },
  summaryField: {
    whiteSpace: 'nowrap',
  },
};

export default ExternalLinkSearchPane;
